import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { TrendingUp, TrendingDown, Minus, BarChart2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AppShell } from '@/shared/ui/AppShell'
import type { GrowthDir, TrendIndexEntry } from '../model/contracts'
import { useTrendIndex } from '../state/useTrendIndex'
import { TopicSparkline } from './TopicSparkline'

const GROWTH_ICON: Record<GrowthDir, { icon: typeof TrendingUp; className: string }> = {
  up: { icon: TrendingUp, className: 'text-sentiment-positive' },
  down: { icon: TrendingDown, className: 'text-sentiment-negative' },
  flat: { icon: Minus, className: 'text-muted-foreground' },
}

function ScoreChip({ score }: { score: number }) {
  const color =
    score >= 70
      ? 'bg-sentiment-positive-surface text-sentiment-positive border-sentiment-positive-border'
      : score >= 40
        ? 'bg-secondary text-foreground border-border'
        : 'bg-sentiment-negative-surface text-sentiment-negative border-sentiment-negative-border'

  return (
    <span
      className={cn(
        'inline-flex min-w-[36px] items-center justify-center rounded-full border px-2 py-0.5 text-[11px] font-semibold tabular-nums',
        color,
      )}
    >
      {score}
    </span>
  )
}

function TrendRow({ entry, rank }: { entry: TrendIndexEntry; rank: number }) {
  const { t } = useTranslation('trends')
  const growth = GROWTH_ICON[entry.growthDir]
  const GrowthIcon = growth.icon

  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 transition-colors hover:border-primary/20">
      <span className="w-5 shrink-0 text-center text-xs text-muted-foreground">
        {rank}
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{entry.name}</p>
        <p className="text-[11px] text-muted-foreground">
          {entry.totalMentions7d} {t('mentions7d')}
        </p>
      </div>

      <TopicSparkline values={entry.sparkline} width={48} height={20} />

      <div
        className="flex items-center gap-1"
        title={t(`growth.${entry.growthDir}`)}
        aria-label={t(`growth.${entry.growthDir}`)}
      >
        <GrowthIcon size={14} strokeWidth={2.5} className={growth.className} />
      </div>

      <div className="flex items-center gap-1">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {t('score')}
        </span>
        <ScoreChip score={entry.score} />
      </div>
    </div>
  )
}

function TrendIndexSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5"
        >
          <div className="h-4 w-4 animate-pulse rounded bg-secondary" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3.5 w-1/2 animate-pulse rounded bg-secondary" />
            <div className="h-3 w-1/4 animate-pulse rounded bg-secondary" />
          </div>
          <div className="h-5 w-12 animate-pulse rounded bg-secondary" />
          <div className="h-5 w-8 animate-pulse rounded bg-secondary" />
        </div>
      ))}
    </div>
  )
}

const TrendIndexContent = memo(function TrendIndexContent() {
  const { t } = useTranslation('trends')
  const { data, isLoading } = useTrendIndex()

  return (
    <div className="mx-auto max-w-2xl space-y-4 py-4 lg:py-6">
      <div className="flex items-center gap-2">
        <BarChart2 size={20} className="text-primary" />
        <h1 className="text-lg font-bold text-foreground">{t('title')}</h1>
      </div>

      {isLoading ? (
        <TrendIndexSkeleton />
      ) : !data || data.topics.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('empty')}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {data.topics.map((entry, i) => (
            <TrendRow key={entry.id} entry={entry} rank={i + 1} />
          ))}
        </div>
      )}
    </div>
  )
})

export function TrendIndexPage() {
  return (
    <AppShell showAnalytics={false}>
      <TrendIndexContent />
    </AppShell>
  )
}
